/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@zapier/design-system';

import { ServiceIconShell } from '../ServiceIconShell';
import { ServiceIconSize } from '../ServiceIcon';

/** Font size depends on the size of the icon and the number to be displayed */
/** Maps the service icon size to a specific font size. */
const fontSizeMapping = (size: ServiceIconSize, text: number) => {
  switch (size) {
    case 20:
      return 10;
    case 30:
      return text > 9 ? 12 : 16;
    case 40:
      return text > 9 ? 16 : 18;
    case 60:
      return text > 9 ? 18 : 24;
    case 80:
      return 32;
    case 120:
      return 48;
    case 150:
      return 60;
    default:
      return 16;
  }
};

/** Typography in v5 depends on the size of the icon */
const typography: { [key: number]: object } = {
  30: Typography.paragraph3Bold,
  40: Typography.paragraph1Bold,
  60: Typography.subHeader1Bold,
};

type ServiceIconNumericProps = {
  /*
   * Number to display within the icon box.
   */
  number: number;
  /**
   * Sets the width and height of the numberic icon. Only a limited selection
   * of sizes support the use of the numeric icon.
   */
  size: ServiceIconSize;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be
   * contained in a container with non primary color background.
   */
  hasBorder?: boolean;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
};

const Styles = labelStyles('ServiceIconNumeric', {
  number: (fontSize: number, iconSize: ServiceIconSize) => ({
    ...typography[iconSize],
    fontSize,
    color: Colors.neutral800,
    textIndent: -2, // Optical alignment
  }),
});

export const ServiceIconNumeric = (props: ServiceIconNumericProps) => {
  const { size, hasBorder = true, hasWhiteBackground } = props;
  const label = `Plus ${props.number} more apps.`;
  const fontSize = fontSizeMapping(size, props.number);
  // If the results are greater than 99, cap the overflow result count at 99+
  const text = props.number > 99 ? `99+` : `+${props.number}`;
  return (
    <ServiceIconShell
      size={size}
      hasBorder={hasBorder}
      hasWhiteBackground={hasWhiteBackground}
    >
      <span aria-label={label} css={Styles.number(fontSize, size)} role="img">
        <span aria-hidden="true">{text}</span>
      </span>
    </ServiceIconShell>
  );
};
